import React, { useEffect, useState, useCallback } from "react";
import { creditsApi } from "../../services/api";
import { Badge, Button, Card, PageHeader, Input } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  Plus,
  Pencil,
  Trash2,
  Star,
  Package,
  Lock,
  X,
  AlertTriangle,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const PACK_TYPES = ["REGULAR", "IND_REGISTRATION_BONUS"];

const EMPTY_FORM = {
  name:               "",
  credits:            100,
  actualPrice:        99,
  discountPercentage: 0,
  popular:            false,
  active:             true,
  packType:           "REGULAR",
};

const FILTER_OPTIONS = [
  { label: "All",      value: null  },
  { label: "Active",   value: true  },
  { label: "Inactive", value: false },
];

const POPULAR_OPTIONS = [
  { label: "All",     value: null  },
  { label: "Popular", value: true  },
  { label: "Regular", value: false },
];

const PACK_TYPE_OPTIONS = [
  { label: "All",        value: null                     },
  { label: "Regular",    value: "REGULAR"                },
  { label: "Reg. Bonus", value: "IND_REGISTRATION_BONUS" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcDiscountPrice(actualPrice, discountPercentage) {
  const p = Number(actualPrice)        || 0;
  const d = Number(discountPercentage) || 0;
  if (d <= 0) return p;
  return Math.round(p - (p * d) / 100);
}

function validatePack(form) {
  if (!form.name?.trim())             return "Pack name is required.";
  if (form.credits <= 0)              return "Credits must be greater than 0.";
  if (form.packType !== "IND_REGISTRATION_BONUS" && form.actualPrice <= 0)
    return "Actual price must be greater than 0.";
  if (form.discountPercentage < 0)    return "Discount % cannot be negative.";
  if (form.discountPercentage >= 100) return "100% discount is not applicable.";
  if (
    form.discountPercentage > 0 &&
    calcDiscountPrice(form.actualPrice, form.discountPercentage) >= form.actualPrice
  ) return "Actual price must be greater than discounted price.";
  if (!form.packType) return "Pack type is required.";
  return null;
}

function packTypeLabel(value) {
  return value === "IND_REGISTRATION_BONUS" ? "Reg. Bonus" : "Regular";
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function PackToggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? "#0F3D3E" : "var(--border)",
        border: `1px solid ${value ? "#0F3D3E" : "var(--border)"}`,
        position: "relative", cursor: "pointer", flexShrink: 0,
        transition: "background 0.2s ease",
      }}
    >
      <div style={{
        position: "absolute", top: 2,
        left: value ? 22 : 2,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.2s ease",
      }} />
    </div>
  );
}

// ─── Filter Pill ──────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
        cursor: "pointer", border: "1px solid",
        borderColor: active ? "#0F3D3E"             : "var(--border)",
        background:  active ? "rgba(15,61,62,0.10)" : "transparent",
        color:       active ? "#0F3D3E"             : "var(--text-muted)",
        transition: "all 0.15s", fontFamily: "var(--font-body)",
      }}
    >
      {label}
    </button>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function PackModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 1000, display: "flex", alignItems: "center",
      justifyContent: "center", padding: 16,
    }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 14, padding: 28,
          maxWidth: 480, width: "100%", maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: "1px solid var(--border)", position: "relative",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 16, color: "var(--text-primary)", margin: 0 }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              width: 32, height: 32, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-muted)", transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.1)"; e.currentTarget.style.color = "#dc2626"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "var(--text-muted)"; }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreditPacks() {
  const [packs,     setPacks]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState({ ...EMPTY_FORM });

  const [search,         setSearch]         = useState("");
  const [searchInput,    setSearchInput]    = useState("");
  const [activeFilter,   setActiveFilter]   = useState(null);
  const [popularFilter,  setPopularFilter]  = useState(null);
  const [packTypeFilter, setPackTypeFilter] = useState(null);

  // ── Load ─────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    creditsApi.getPacks(search, activeFilter, popularFilter, packTypeFilter)
      .then(r => {
        const raw = r.data.data?.content ?? r.data.data ?? [];
        const tot = r.data.data?.totalElements ?? raw.length;
        setTotal(tot);
        const sorted = [...raw].sort((a, b) => {
          if (b.popular !== a.popular) return b.popular ? 1 : -1;
          return (b.discountPrice ?? b.actualPrice) - (a.discountPrice ?? a.actualPrice);
        });
        setPacks(sorted);
      })
      .catch(() => { setPacks([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [search, activeFilter, popularFilter, packTypeFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch      = (e) => { e.preventDefault(); setSearch(searchInput.trim()); };
  const handleClearSearch = ()  => { setSearchInput(""); setSearch(""); };

  // ── Modal ─────────────────────────────────────────────────────────────────
  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowModal(true); };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name:               p.name,
      credits:            p.credits,
      actualPrice:        p.actualPrice,
      discountPercentage: p.discountPercentage,
      popular:            p.popular,
      active:             p.active,
      packType:           p.packType ?? "REGULAR",
    });
    setShowModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "packType" && value === "IND_REGISTRATION_BONUS") {
        updated.actualPrice        = 0;
        updated.discountPercentage = 0;
        updated._discountRaw       = undefined;
        updated.discountPrice      = 0;
        return updated;
      }
      if (field === "actualPrice" || field === "discountPercentage") {
        const ap = field === "actualPrice"        ? Number(value) : Number(prev.actualPrice);
        const dp = field === "discountPercentage" ? Number(value) : Number(prev.discountPercentage);
        updated.discountPrice = calcDiscountPrice(ap, dp);
      }
      return updated;
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      name:               form.name,
      credits:            Number(form.credits),
      actualPrice:        Number(form.actualPrice),
      discountPercentage: Number(form.discountPercentage),
      popular:            form.popular,
      active:             form.active,
      packType:           form.packType,
    };
    const err = validatePack(payload);
    if (err) { showToast(err, "error"); return; }

    try {
      if (editing) await creditsApi.updatePack(editing, payload);
      else         await creditsApi.createPack(payload);
      showToast(editing ? "Pack updated successfully" : "Pack created successfully");
      setShowModal(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || "Something went wrong", "error");
    }
  };

  // ── Toggle Active ─────────────────────────────────────────────────────────
  const handleToggleActive = async (p) => {
    setPacks(prev => prev.map(pack => pack.id === p.id ? { ...pack, active: !p.active } : pack));
    try {
      await creditsApi.togglePack(p.id, !p.active);
      showToast(`Pack ${!p.active ? "enabled" : "disabled"} successfully`);
    } catch {
      setPacks(prev => prev.map(pack => pack.id === p.id ? { ...pack, active: p.active } : pack));
      showToast("Failed to update pack status", "error");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pack? This cannot be undone.")) return;
    try {
      await creditsApi.deletePack(id);
      showToast("Pack deleted");
      load();
    } catch {
      showToast("Failed to delete pack", "error");
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const savings = (p) => {
    if (p.discountPercentage <= 0) return null;
    const saved = Number((p.actualPrice - p.discountPrice).toFixed(2));
    const pct   = Number(p.discountPercentage.toFixed(2));
    return `Save ₹${saved} (${pct}% off)`;
  };

  const previewDiscountPrice = calcDiscountPrice(Number(form.actualPrice), Number(form.discountPercentage));
  const showPriceWarning     = Number(form.discountPercentage) > 0 && previewDiscountPrice >= Number(form.actualPrice);
  const hasFilters           = search || activeFilter !== null || popularFilter !== null || packTypeFilter !== null;
  const isBonus              = form.packType === "IND_REGISTRATION_BONUS";

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <PageHeader
        title="Credit Packs"
        subtitle="Manage purchasable credit packages shown in the app"
        action={
          <Button onClick={openCreate}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} strokeWidth={2.5} /> New Pack
            </span>
          </Button>
        }
      />

      {/* Toolbar */}
      <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Search bar */}
        <form onSubmit={handleSearch} style={{ display: "flex", gap: 10 }}>
          <input
            placeholder="Search by pack name…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              flex: 1, padding: "9px 14px", borderRadius: "var(--radius)",
              border: "1px solid var(--border)", background: "var(--bg-input)",
              color: "var(--text-primary)", fontSize: 13,
              fontFamily: "var(--font-body)", outline: "none",
            }}
          />
          <button type="submit" style={{
            padding: "9px 20px", borderRadius: "var(--radius)", border: "none",
            background: "#0F3D3E", color: "#fff", fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: "var(--font-body)",
          }}>Search</button>
          {(searchInput || search) && (
            <button type="button" onClick={handleClearSearch} style={{
              padding: "9px 16px", borderRadius: "var(--radius)",
              border: "1px solid var(--border)", background: "transparent",
              color: "var(--text-secondary)", fontSize: 13, cursor: "pointer",
              fontFamily: "var(--font-body)",
            }}>Clear</button>
          )}
        </form>

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 4 }}>Status:</span>
          {FILTER_OPTIONS.map(opt => (
            <FilterPill key={String(opt.value)} label={opt.label} active={activeFilter === opt.value} onClick={() => setActiveFilter(opt.value)} />
          ))}

          <span style={{ width: 1, height: 20, background: "var(--border)", margin: "0 6px" }} />

          <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 4 }}>Popular:</span>
          {POPULAR_OPTIONS.map(opt => (
            <FilterPill key={String(opt.value)} label={opt.label} active={popularFilter === opt.value} onClick={() => setPopularFilter(opt.value)} />
          ))}

          <span style={{ width: 1, height: 20, background: "var(--border)", margin: "0 6px" }} />

          <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 4 }}>Type:</span>
          {PACK_TYPE_OPTIONS.map(opt => (
            <FilterPill key={String(opt.value)} label={opt.label} active={packTypeFilter === opt.value} onClick={() => setPackTypeFilter(opt.value)} />
          ))}

          {hasFilters && (
            <button
              type="button"
              onClick={() => { setSearch(""); setSearchInput(""); setActiveFilter(null); setPopularFilter(null); setPackTypeFilter(null); }}
              style={{ marginLeft: 4, fontSize: 11, color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "var(--font-body)" }}
            >
              Reset all
            </button>
          )}
        </div>

        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {loading ? "Loading…" : `${total} pack${total !== 1 ? "s" : ""} found`}
        </div>
      </div>

      {/* Pack Grid */}
      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>
          <div style={{ width: 32, height: 32, margin: "0 auto 12px", border: "3px solid var(--border)", borderTop: "3px solid #0F3D3E", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          Loading packs…
        </div>
      ) : packs.length === 0 ? (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <Package size={32} strokeWidth={1.5} color="var(--text-muted)" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>No credit packs found</div>
          <div style={{ fontSize: 12 }}>
            {hasFilters ? "Try adjusting your filters." : "Create your first pack to get started."}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 16 }}>
          {packs.map(p => (
            <Card key={p.id} style={{ position: "relative", display: "flex", flexDirection: "column" }}>

              {/* Top badges row */}
              <div style={{ minHeight: 24, marginBottom: 6, display: "flex", gap: 6 }}>
                {p.popular && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "linear-gradient(135deg,#d4a017,#e8b84b)",
                    color: "#0d1f1f", fontSize: 10, fontWeight: 700,
                    padding: "3px 8px", borderRadius: 6, letterSpacing: "0.5px",
                  }}>
                    <Star size={9} strokeWidth={2.5} style={{ fill: "#0d1f1f" }} /> POPULAR
                  </div>
                )}
                {p.packType && p.packType !== "REGULAR" && (
                  <div style={{
                    display: "inline-block",
                    background: "rgba(99,102,241,0.12)", color: "#6366f1",
                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6,
                    letterSpacing: "0.5px", border: "1px solid rgba(99,102,241,0.25)",
                  }}>
                    {packTypeLabel(p.packType)}
                  </div>
                )}
              </div>

              {/* Name + status */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 14, color: "var(--text-primary)", paddingRight: 8 }}>
                  {p.name}
                </div>
                <Badge variant={p.active ? "success" : "danger"} style={{ flexShrink: 0 }}>
                  {p.active ? "Active" : "Inactive"}
                </Badge>
              </div>

              {/* Credits */}
              <div style={{ marginBottom: 14, flex: 1 }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "var(--gold-400)", fontFamily: "var(--font-display)", lineHeight: 1 }}>
                  {p.credits?.toLocaleString("en-IN")}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>credits</div>
              </div>

              {/* Price */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: "var(--teal-300)" }}>
                  ₹{p.discountPercentage > 0 ? p.discountPrice : p.actualPrice}
                </span>
                {p.discountPercentage > 0 && (
                  <span style={{ fontSize: 13, color: "var(--text-muted)", textDecoration: "line-through" }}>
                    ₹{p.actualPrice}
                  </span>
                )}
              </div>

              {/* Savings */}
              <div style={{ minHeight: 20, marginBottom: 14 }}>
                {savings(p) && <div style={{ fontSize: 11, color: "var(--green)" }}>{savings(p)}</div>}
              </div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: 10, borderTop: "1px solid var(--border)" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Pencil size={12} strokeWidth={2} /> Edit
                    </span>
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(p.id)}>
                    <Trash2 size={13} strokeWidth={2} />
                  </Button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {p.active ? "Enabled" : "Disabled"}
                  </span>
                  <PackToggle value={p.active} onChange={() => handleToggleActive(p)} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <PackModal open={showModal} onClose={() => setShowModal(false)} title={editing ? "Edit Pack" : "Create Pack"}>
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          <Input label="Pack Name" value={form.name} onChange={e => handleFormChange("name", e.target.value)} required />

          <Input label="Credits" type="number" min="0" value={form.credits} onChange={e => handleFormChange("credits", e.target.value)} required />

          <Input
            label="Actual Price (₹)"
            type="number" min="0"
            value={form.actualPrice}
            onChange={e => handleFormChange("actualPrice", e.target.value)}
            required
            disabled={isBonus}
            style={isBonus ? { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" } : {}}
          />

          {/* Discount % */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: isBonus ? "var(--text-muted)" : "var(--text-secondary)" }}>
              Discount %
            </label>
            <input
              type="number" min="0" max="99"
              disabled={isBonus}
              value={
                form.discountPercentage === 0 && form._discountRaw !== undefined
                  ? form._discountRaw
                  : form.discountPercentage
              }
              onChange={e => {
                if (isBonus) return;
                const raw = e.target.value;
                const num = raw === "" ? 0 : Math.max(0, Math.min(99, Number(raw)));
                setForm(prev => ({
                  ...prev,
                  discountPercentage: num,
                  _discountRaw: raw,
                  discountPrice: calcDiscountPrice(Number(prev.actualPrice), num),
                }));
              }}
              onBlur={e => {
                if (isBonus) return;
                const num = e.target.value === "" ? 0 : Math.max(0, Math.min(100, Number(e.target.value)));
                setForm(prev => ({ ...prev, discountPercentage: num, _discountRaw: undefined }));
              }}
              style={{
                width: "100%", padding: "8px 12px",
                border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                background: isBonus ? "var(--bg-input)" : "var(--bg-card)",
                color: "var(--text-primary)", fontSize: 14,
                fontFamily: "var(--font-body)", outline: "none",
                opacity: isBonus ? 0.45 : 1,
                cursor: isBonus ? "not-allowed" : "text",
                pointerEvents: isBonus ? "none" : "auto",
              }}
            />
          </div>

          {/* Auto-calculated discounted price */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: isBonus ? "var(--text-muted)" : "var(--text-secondary)" }}>
                Discounted Price (₹)
              </label>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                background: "rgba(15,61,62,0.08)", color: "#0F3D3E",
                border: "1px solid rgba(15,61,62,0.15)", letterSpacing: "0.3px",
              }}>AUTO</span>
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 12px", borderRadius: "var(--radius-md)",
              background: isBonus ? "var(--bg-input)" : "rgba(15,61,62,0.05)",
              border: `1px dashed ${isBonus ? "var(--border)" : "rgba(15,61,62,0.25)"}`,
              opacity: isBonus ? 0.45 : 1,
            }}>
              <Lock size={13} strokeWidth={2.5} color="#0F3D3E" style={{ flexShrink: 0, opacity: 0.6 }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: "#0F3D3E" }}>
                ₹{isBonus ? 0 : previewDiscountPrice}
              </span>
              <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>
                {isBonus
                  ? "Not applicable for Reg. Bonus"
                  : Number(form.discountPercentage) > 0
                    ? `${form.discountPercentage}% off ₹${form.actualPrice}`
                    : "No discount applied"}
              </span>
            </div>
            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
              {isBonus
                ? "Price fields are locked for Registration Bonus packs"
                : "Calculated automatically — Actual Price × (1 − Discount %)"}
            </span>
          </div>

          {/* Price warning */}
          {showPriceWarning && (
            <div style={{
              fontSize: 12, color: "#dc2626",
              background: "rgba(239,68,68,0.07)",
              padding: "8px 12px", borderRadius: 8,
              border: "1px solid rgba(239,68,68,0.2)",
              display: "flex", alignItems: "center", gap: 7,
            }}>
              <AlertTriangle size={14} strokeWidth={2} color="#dc2626" style={{ flexShrink: 0 }} />
              Discounted price must be less than actual price when a discount is applied.
            </div>
          )}

          {/* Pack Type selector */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Pack Type</label>
            <select
              value={form.packType}
              onChange={e => handleFormChange("packType", e.target.value)}
              style={{
                width: "100%", padding: "8px 12px",
                border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                background: "var(--bg-card)", color: "var(--text-primary)",
                fontSize: 14, fontFamily: "var(--font-body)", outline: "none", cursor: "pointer",
              }}
            >
              {PACK_TYPES.map(t => (
                <option key={t} value={t}>{packTypeLabel(t)}</option>
              ))}
            </select>
          </div>

          {/* Toggles */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--border)" }}>
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Mark as Popular</span>
            <PackToggle value={!!form.popular} onChange={v => handleFormChange("popular", v)} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Active</span>
            <PackToggle value={!!form.active} onChange={v => handleFormChange("active", v)} />
          </div>

          {/* Submit */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
            <Button variant="ghost" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">{editing ? "Update Pack" : "Create Pack"}</Button>
          </div>
        </form>
      </PackModal>
    </div>
  );
}